import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import isEmpty from 'lodash/fp/isEmpty'
import { ICustomEventProps, IProduct, NodeType, SA_TYPE } from '../../types/types'
import slideoverCloseStyles from '../../constants/slideoverCloseStyles'
import { DEFAULT_SUPPLIER_ID, METHOD_POST, METHOD_PUT, URLs } from '../../constants'
import { SPECIAL_INGREDIENT } from '../../constants/toastNotifications'
import { UserContext } from '../../contexts/UserProvider'
import { SideloadCertificateContext } from '../../contexts/SideloadCertificateProvider'
import getURL from '../../utils/getURL'
import useFetch from '../../hooks/useFetch'
import { useTopCenterToast } from '../../hooks/useTopCenterToast'
import useUpdateFormData from '../../hooks/useUpdateFormData'
import { ISlideoverProps } from '../../types/slideover-types'
import SlideoverAlert from '../../product-overiew/SlideoverAlert'
import { PageLoader } from '../../components'
import Slideover from '../components/Slideover'
import { ISkuInputRef } from '../../components/SkuIdInput'
import SlideoverProvider from '../../contexts/SlideoverProvider'
import SpecialIngredientForm from './SpecialIngredientForm'


const DEFAULT_DATA: any = {
    emissionFactorCode: '',
    emissionFactorCountryCode: '',
    originCountryCode: '',
    supplierId: DEFAULT_SUPPLIER_ID,
}

const formId = 'add-edit-splIngredient-form'

interface ISpecialIngredientProps extends Omit<ISlideoverProps, 'type'> {
    allowExisting?: boolean
    onSaved: (splIngredient: IProduct) => void
    productId?: string
}

export default function SpecialIngredient({
    allowExisting = false,
    companyId,
    isOpen,
    onClose,
    onSaved,
    productId,
}: ISpecialIngredientProps) {
    const { openSideload } = useContext(SideloadCertificateContext)
    const skuRef = useRef<ISkuInputRef>(null)
    const [splIngredient, setSplIngredient] = useState<IProduct>(DEFAULT_DATA)
    const [errors, setErrors] = useState({})
    const [isExisting, setIsExisting] = useState(false)
    const [splIngChanged, setSplIngredientChanged] = useState(false)
    const { showError, showSuccess } = useTopCenterToast()

    const { company } = useContext(UserContext)
    const companyName = company?.name || ''

    const isEdit = !isEmpty(productId)

    const { data: defaultProduct, error, loading, refetch } = useFetch({
        autoFetch: isEdit && isOpen,
        url: getURL(URLs.Product.GET_PRIMARY, { companyId, productId }),
    })

    useEffect(() => {
        if (!isEmpty(error)) {
            showError({ description: SPECIAL_INGREDIENT.ERROR })
            handleClose()
        }
    }, [error])

    useEffect(() => {
        if (!isEmpty(defaultProduct)) {
            setSplIngredient(defaultProduct)
        }
    }, [defaultProduct])

    const onUpdate = (data: any) => {
        showSuccess({
            description:
                isEdit || (isExisting && splIngChanged)
                    ? SPECIAL_INGREDIENT.UPDATE
                    : SPECIAL_INGREDIENT.CREATE,
        })
        onSaved(data)
        handleClose()
    }

    const url
        = isEdit || (isExisting && splIngChanged)
            ? getURL(URLs.Product.UPDATE, { companyId, productId: productId || splIngredient.id })
            : getURL(URLs.Product.ADD, { companyId })

    const { saving, update } = useUpdateFormData({
        method: isEdit || (isExisting && splIngChanged) ? METHOD_PUT : METHOD_POST,
        onUpdate,
        url,
    })

    const handleChange = (changes: any, reset = false) => {
        setSplIngredient((state: any) =>
            reset ? { ...DEFAULT_DATA, ...changes } : { ...state, ...changes }
        )
        setErrors({})
        if (reset) {
            setIsExisting(false)
        }
    }
    const handleSubmit = (event: any) => {
        const { taxonomyCode, name, originCountryCode, sku, supplierId } = splIngredient
        const isValidSku = skuRef?.current?.isValidSku()
        event.preventDefault()
        event.stopPropagation()

        if (isEmpty(supplierId)) {
            setErrors({
                supplier: 'Supplier should not be blank',
            })
            return
        }
        if (isEmpty(taxonomyCode)) {
            setErrors({
                taxonomyCode: 'Category should not be blank',
            })
            return
        }
        if (isEmpty(name)) {
            setErrors({
                splIngredient: 'Name should not be blank',
            })
            return
        }
        if (!isExisting && !isValidSku && !isEmpty(sku)) {
            setErrors({
                sku: 'Invalid SKU',
            })
            return
        }
        if (isEmpty(originCountryCode)) {
            setErrors({
                originCountryCode: 'Country should not be blank',
            })
            return
        }
        save()
    }

    function save() {
        if (isExisting) {
            onSaved(splIngredient)
            handleClose()
        } else {
            update({
                ...splIngredient,
                id: isEdit ? productId : undefined,
                label: undefined,
                saType: SA_TYPE.SpecialIngredient,
                updatedDate: undefined,
            })
        }
    }

    const handleSetSplIngredient = useCallback((option: any) => {
        if (option && option.id) {
            setIsExisting(true)
            setErrors({})
            setSplIngredient(option)
            refetch(getURL(URLs.Product.GET_PRIMARY, { companyId, productId: option.id }))
        }
    }, [])

    function handleClose() {
        setSplIngredient(DEFAULT_DATA)
        setErrors({})
        setIsExisting(false)
        onClose && onClose(NodeType.SPECIAL_INGREDIENT)
    }

    function handleSplIngNameChange(event: ICustomEventProps) {
        const { name, value } = event.target

        if (isEmpty(value)) {
            handleChange({ id: undefined, [name]: value, sku: '' })
        } else {
            handleChange({ id: undefined, [name]: value })
        }

        if (isExisting) {
            setTimeout(() => {
                skuRef?.current?.validateSku()
            }, 100)
        }
        setIsExisting(false)
    }

    function handleSideloadClick() {
        openSideload({
            onSuccess: onSaved,
            url: getURL(URLs.Certificate.SIDELOAD, { companyId }, `?productId=${productId}`),
        })
    }
    return (
        <SlideoverProvider companyId={companyId}>
            <Slideover
                wider="max-w-3xl"
                isOpen={isOpen}
                type={NodeType.SPECIAL_INGREDIENT}
                headerProps={{
                    iconProps: slideoverCloseStyles as any,
                }}
                title={isEdit ? 'Update Special Ingredient' : 'Add Special Ingredient'}
                formId={formId}
                saving={saving}
                disableSave={(!allowExisting && isExisting) || loading}
                onClose={handleClose}
                saveButtonText={
                    saving
                        ? isEdit
                            ? 'Updating...'
                            : 'Creating...'
                        : isExisting
                            ? 'Add special ingredient'
                            : isEdit
                                ? 'Update special ingredient'
                                : 'Create special ingredient'
                }
                footerChildren={
                    <div className="flex items-center">
                        {!allowExisting && isExisting && (
                            <SlideoverAlert
                                title="Special ingredient already exists, can not save"
                                description="To add a new special ingredient you must change the name to proceed."
                            />
                        )}
                    </div>
                }
                footerProps={{
                    className: 'flex flex-row justify-between w-full',
                }}
            >
                {loading && (
                    <PageLoader centered className="mt-4 absolute top-20 left-1/2 bg-white" />
                )}
                {isOpen && (
                    <SpecialIngredientForm
                        ref={skuRef}
                        formId={formId}
                        editMode={isEdit}
                        readOnly={isExisting}
                        errors={errors}
                        companyId={companyId}
                        companyName={companyName}
                        autoFetch={isOpen}
                        splIngredient={splIngredient}
                        onChange={handleChange}
                        onSplIngNameChange={handleSplIngNameChange}
                        onSplIngSelect={handleSetSplIngredient}
                        onSumbit={handleSubmit}
                        onSideloadClick={handleSideloadClick}
                        setSplIngredientChanged={setSplIngredientChanged}
                    />
                )}
            </Slideover>
        </SlideoverProvider>
    )
}
